<template>
  <Container>
    <Title
      strong
      bold
    >
      {{ donationPeriod }}
    </Title>

    <Form>
      <FormCard>
        <BreadCrumb :items="breadCrumbitems" />
        <FormHeading>
          {{ steps.DONATION_INFO }}
        </FormHeading>
        <p :class="$style.text">
          姓名會作為未來捐款記錄查詢，確認後無法修改，建議儘量使用本名。
        </p>

        <FormRow>
          <template>
            <FormLabel for="donation-owner">
              捐款名義
            </FormLabel>
            <SelectInput
              id="donation-owner"
              :options="donationOwnerOptions"
              :value.sync="donationOwner"
              :error="donationOwnerError"
            />
          </template>
        </FormRow>

        <template v-if="donationOwner === '團體名義'">
          <FormRow>
            <FormLabel for="group-title">
              單位名稱
            </FormLabel>
            <TextInput
              id="group-title"
              :value.sync="contact.groupName"
              :error="contactGroupTitleError"
            />
          </FormRow>

          <FormRow>
            <FormLabel for="receipt-id-no">
              統編
            </FormLabel>
            <FormLabel
              right
              small
            >
              非本國籍個人之身分證字號及團體統一編號無自動上傳國稅局功能，請索取紙本收據並自行辦理扣繳申報。
            </FormLabel>
            <TextInput
              id="receipt-id-no"
              upper-case
              :value.sync="receipt.idNo"
              :error="receiptIdNoError"
            />
          </FormRow>
        </template>

        <template v-if="donationOwner !== '匿名'">
          <FormRow>
            <FormLabel for="last-name">
              聯絡人姓氏
            </FormLabel>
            <TextInput
              id="last-name"
              placeholder="王"
              :value.sync="contact.lastName"
              :error="contactLastNameError"
            />
          </FormRow>

          <FormRow>
            <FormLabel for="first-name">
              聯絡人名字
            </FormLabel>
            <TextInput
              id="first-name"
              placeholder="大明"
              :value.sync="contact.firstName"
              :error="contactFirstNameError"
            />
          </FormRow>

          <FormRow>
            <FormSubTitle required>
              聯絡資訊
            </FormSubTitle>
          </FormRow>

          <FormRow>
            <template>
              <FormLabel for="email">
                <span v-if="donationOwner === '團體名義'"> 聯絡人</span>Email
              </FormLabel>
              <TextInput
                id="email"
                placeholder="tft@gmail.com"
                :value.sync="contact.email"
                :error="contactEmailError"
              />
            </template>
          </FormRow>

          <FormRow>
            <template>
              <FormLabel for="phone">
                手機號碼
              </FormLabel>
              <FormLabel
                right
                small
              >
                手機為查詢捐款紀錄使用，建議輸入正確號碼。格式範例：0912345678、886912345678
              </FormLabel>
              <TelInput
                id="phone"
                placeholder="0912345678"
                :value.sync="contact.phone"
                :error="contactPhoneError"
              />
            </template>
          </FormRow>
        </template>

        <template v-if="donationOwner !== '匿名'">
          <FormRow>
            <FormSubTitle required>
              收據寄送
            </FormSubTitle>
            <p
              v-if="donationPeriod === donationPeriodType.DONATION_PERIOD"
              :class="$style.text"
            >
              「單筆電子收據」將於每月扣款成功當下隨即以 Email 寄送，「年度紙本收據」或「年度電子收據」將於每年 2、3 月期間寄送。
            </p>
            <p
              v-if="donationPeriod === donationPeriodType.DONATION_SINGLE"
              :class="$style.text"
            >
              「單筆電子收據」將於扣款成功當下隨即以 Email 寄送，<span v-if="donationOwner ==='團體名義'">「單筆紙本收據」將於隔月 10 號統一寄出，</span>「年度紙本收據」將於每年 2、3 月期間寄送。
            </p>
          </FormRow>

          <FormRow>
            <FormLabel for="receipt-type">
              收據種類
            </FormLabel>
            <SelectInput
              id="receipt-type"
              :options="receiptTypes"
              :value.sync="receipt.type"
              :error="receiptTypeError"
            />
          </FormRow>

          <template
            v-if="[ receiptType.SINGLE_ELEC_RECEIPT,
                    receiptType.SINGLE_PAPER_RECEIPT,
                    receiptType.YEAR_ELEC_RECEIPT,
                    receiptType.YEAR_PAPER_RECEIPT].includes(receipt.type)"
          >
            <FormRow>
              <ContentButton
                type="import"
                :label="donationOwner === '個人名義' ? '同上方聯絡人姓名，點擊自動帶入「收據抬頭」':'同上方單位名稱、統編，點擊自動帶入「收據抬頭」、「收據統編/身分證字號」'"
                @click.native="loadDonationUser"
              />
            </FormRow>
            <FormRow>
              <FormLabel for="receipt-title">
                收據抬頭
              </FormLabel>
              <TextInput
                id="receipt-title"
                placeholder="王大明"
                :value.sync="receipt.title"
                :error="receiptTitleError"
              />
            </FormRow>
          </template>

          <FormRow
            v-if="donationOwner === '個人名義' || (donationOwner === '團體名義' && receipt.type !== '不需要收據')"
          >
            <FormLabel for="receipt-id-no">
              收據統編 / 身分證字號
            </FormLabel>
            <FormLabel
              right
              small
            >
              非本國籍個人之身分證字號及團體統一編號無自動上傳國稅局功能，請索取紙本收據並自行辦理扣繳申報。
            </FormLabel>
            <TextInput
              id="receipt-id-no"
              upper-case
              :value.sync="receipt.idNo"
              :error="receiptIdNoError"
            />
            <CheckBoxRadioInput
              v-if="donationOwner === '個人名義'"
              id="upload-etax"
              label="自動上傳國稅局抵減所得稅額"
              :disabled="!validTwId"
              :value.sync="receipt.etaxCheck"
            />
          </FormRow>

          <template
            v-if="[ receiptType.SINGLE_PAPER_RECEIPT,
                    receiptType.YEAR_PAPER_RECEIPT].includes(receipt.type)"
          >
            <!-- mailing address city -->
            <FormRow>
              <FormLabel for="address-city">
                縣市(請選擇)
              </FormLabel>
              <SelectInput
                id="address-city"
                :options="cities"
                :value.sync="receipt.mailingCity"
                :error="receiptMailingCityError"
              />
            </FormRow>
            <!-- mailing address district -->
            <FormRow v-if="receipt.mailingCity">
              <FormLabel for="address-district">
                鄉鎮市區
              </FormLabel>
              <SelectInput
                id="address-district"
                :value.sync="receipt.mailingDistrict"
                :options="districts"
                :error="receiptMailingDistrictError"
                @change="receipt.mailingZipCode = zipCode"
              />
            </FormRow>
            <FormRow>
              <FormLabel for="address-zipcode">
                郵遞區號
              </FormLabel>
              <TextInput
                id="address-zipcode"
                placeholder="100"
                :disabled="true"
                :value.sync="receipt.mailingZipCode"
                :error="receiptZipCodeError"
              />
            </FormRow>
            <!-- mailing detail address-->
            <FormRow>
              <FormLabel for="address-detail">
                詳細地址
              </FormLabel>
              <TextInput
                id="address-detail"
                placeholder="寶慶路25號2樓"
                :value.sync="receipt.mailingAddress"
                :error="receiptMailingAddressError"
              />
            </FormRow>
          </template>

          <FormRow>
            <FormSubTitle required>
              其他
            </FormSubTitle>
          </FormRow>
        </template>

        <template v-if="donationOwner === '匿名'">
          <FormRow>
            <p :class="$style.text">
              若匿名捐款，TFT會以無名氏名義開立收據，但收據將不被寄出，捐款紀錄將也無法被查詢。
            </p>
          </FormRow>
        </template>

        <template v-if="donationOwner !== '匿名'">
          <CheckBoxRadioInput
            id="personal-donation-list-display-name"
            :value.sync="showOnDonationList"
            :error="showOnDonationListError"
            :class="[
              checkBoxRadioInputStyle.left,
            ]"
          >
            <span
              :class="[
                checkBoxRadioInputStyle.label,
                showOnDonationListError && checkBoxRadioInputStyle.error,
              ]"
            >
              我同意 TFT 依財團法人法第 25 條公開捐贈者姓名及金額之規定(若不同意公開也須勾選，並依照此
              <a @click="openOverlay('DonationListGuideline')">說明</a>簽署與回傳聲明書)
            </span>
          </CheckBoxRadioInput>

          <CheckBoxRadioInput
            id="personal-data-consent"
            :value.sync="personalDataConsent"
            :error="personalDataConsentError"
            :class="[
              checkBoxRadioInputStyle.left,
            ]"
          >
            <span
              :class="[
                checkBoxRadioInputStyle.label,
                personalDataConsentError && checkBoxRadioInputStyle.error,
              ]"
            >
              我已詳閱並同意「<a
                href="https://drive.google.com/file/d/1bVdy883rsGU087rI10oerxQrCyvUYJg0/view"
                target="_blank"
                rel="noopener"
              >為台灣而教教育基金會個資蒐集同意書</a>」。
            </span>
          </CheckBoxRadioInput>

          <CheckBoxRadioInput
            id="personal-notification"
            :value.sync="preferredNotification"
            :class="[
              checkBoxRadioInputStyle.left,
            ]"
          >
            <span
              :class="[
                checkBoxRadioInputStyle.label,
              ]"
            >
              我願意收到 TFT 相關資訊
            </span>
          </CheckBoxRadioInput>
        </template>

        <Align space-between>
          <Button
            back
            :small="isMobile"
            @click.native="back()"
          >
            上一步
          </Button>
          <Button
            :small="isMobile"
            @click.native="submit()"
          >
            下一步
          </Button>
        </Align>
        <Align right>
          <Error>
            {{ errorMessage }}
          </Error>
        </Align>
      </FormCard>
    </Form>

    <transition
      name="fade"
      mode="out-in"
    >
      <Overlay
        :is-open="showDonationListGuideline"
        @close="closeOverlay('DonationListGuideline')"
      >
        <OverlayTitle>財團法人法第 25 條</OverlayTitle>
        <div style="margin-top: 15px; font-size: 1.2rem;">
          依財團法人法第 25 條規定，除捐款人事先表示反對外，本會需主動公開捐贈者姓名及金額。倘若捐款人不同意將捐款者姓名公開揭露，請下載「<a
            target="_blank"
            style="color:#e73273"
            href="https://drive.google.com/file/d/1Wjy7c5omeZk-chz25qfPg210UtZRJ_y0/view?usp=sharing"
          >不同意捐款者姓名公開揭露聲明書</a>」，簽名回傳至本會，以利後續作業進行。選擇不公開之捐款人，將以「王O明」方式揭露。未回覆聲明書之捐款人，依照法條規定「將捐款者姓名公開揭露」。
          <br>
          <br>
          填寫完畢後，請務必回覆掃描後 E-mail 至：fin@teach4taiwan.org 標題「不同意捐款者姓名公開揭露聲明書」或郵寄至：100 台北市中正區寶慶路 25 號 2 樓（財團法人為台灣而教教育基金會 財務組 收）」。
          <br>
          <br>
          如有任何問題歡迎 E-mail 至：fin@teach4taiwan.org 或來電 02-23456225，聯絡財務組。
        </div>
      </Overlay>
    </transition>
  </Container>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';
import { mapFields } from 'vuex-map-fields';
import * as Sentry from '@sentry/vue';
import donationPeriodType from '@/configs/donationPeriod';
import receiptType from '@/configs/receiptType';
// configs
import steps from '@/configs/steps';
import {
  isValidString,
  isValidPhone,
  isValidEmail,
  isValidID,
  isValidTwID,
  isValidName,
  isValidAddress,
  isValidOption,
} from '../../utilities/validators';
import { saveStore } from '@/lib/storage';
import { DONATION_WAY_TO_DONATION_GA_INFO } from '@/configs/GAconfig.js';
import { pushUserDataToDataLayer } from '@/helpers/meta';

import Container from '../../components/layout/Container';
import Align from '../../components/layout/Align';
import Title from '../../components/Title';

import Form from '../../components/form/Form';
import FormCard from '../../components/form/FormCard';
import FormHeading from '../../components/form/FormHeading';
import FormLabel from '../../components/form/FormLabel';
import FormRow from '../../components/form/FormRow';
import FormSubTitle from '../../components/form/FormSubTitle';

import TextInput from '../../components/inputs/TextInput';
import TelInput from '@/components/inputs/TelInput';
import SelectInput from '../../components/inputs/SelectInput';
import CheckBoxRadioInput from '../../components/inputs/CheckBoxRadioInput';
import ContentButton from '../../components/buttons/ContentButton';
import Button from '../../components/buttons/Button';

import BreadCrumb from '../../components/others/BreadCrumb';
import Error from '../../components/others/Error';

import Overlay from '../../components/overlay/Overlay';
import OverlayTitle from '../../components/overlay/OverlayTitle';

import { taiwan_district } from '../../utilities/administrativeDistric';

// styles
import checkBoxRadioInputStyle from '../../styles/06-components/_components.CheckBoxRadioInput.module.scss';

export default {
  components: {
    Container,
    Align,
    Title,
    Form,
    FormCard,
    FormHeading,
    FormRow,
    FormSubTitle,
    FormLabel,
    Button,
    ContentButton,
    TextInput,
    TelInput,
    SelectInput,
    CheckBoxRadioInput,
    Overlay,
    OverlayTitle,
    BreadCrumb,
    Error,
  },

  data() {
    return {
      steps: Object.freeze(steps),
      donationPeriodType: Object.freeze(donationPeriodType),
      receiptType: Object.freeze(receiptType),
      donationOwnerError: '',
      contactGroupTitleError: '',
      contactLastNameError: '',
      contactFirstNameError: '',
      contactEmailError: '',
      contactPhoneError: '',

      receiptTypeError: '',
      receiptTitleError: '',
      receiptIdNoError: '',
      receiptMailingCityError: '',
      receiptMailingDistrictError: '',
      receiptZipCodeError: '',
      receiptMailingAddressError: '',
      showOnDonationListError: false,

      personalDataConsent: null,
      personalDataConsentError: false,
      validTwId: true,
      checkBoxRadioInputStyle,
    };
  },

  computed: {
    ...mapState({
      breadCrumbitems: state => state.breadCrumb.items,
      showDonationListGuideline: state => state.overlay.showDonationListGuideline,
    }),

    ...mapFields('donation', [
      'donationWay',
      'amount',
      'donationPeriod',
      'donationOwner',
      'contact',
      'receipt',
      'contactIsGroup',
      'showOnDonationList',
      'preferredNotification',
    ]),

    ...mapGetters('ui', [
      'isMobile',
    ]),

    receiptTypes: function() {
      const allReceiptTypes = [receiptType.SINGLE_ELEC_RECEIPT,
        this.donationOwner == '團體名義' ? receiptType.SINGLE_PAPER_RECEIPT : null,
        this.donationPeriod == donationPeriodType.DONATION_PERIOD ? receiptType.YEAR_ELEC_RECEIPT : null,
        receiptType.YEAR_PAPER_RECEIPT,
        '不需要收據',
      ];
      const receiptTypes = allReceiptTypes.filter(item=> item !== null);
      return receiptTypes;
    },

    donationOwnerOptions: function() {
      const donationOwnerOptions = ['個人名義', '團體名義'];
      if(this.donationPeriod == donationPeriodType.DONATION_SINGLE) {
        donationOwnerOptions.push('匿名');
      }

      return donationOwnerOptions;
    },

    cities: function() {
      let cities = [];
      cities = taiwan_district.map(item=>item.name);
      return cities;
    },

    districts: function() {
      const result = this.receipt.mailingCity ? taiwan_district.filter(item=>item.name == this.receipt.mailingCity) : [];
      const district_names = this.receipt.mailingCity ? result[0].districts.map(item=>item.name) : [];
      return district_names;
    },

    zipCode: function() {
      const result = taiwan_district.filter(item=>item.name == this.receipt.mailingCity);
      const district = result[0].districts.filter(item => item.name == this.receipt.mailingDistrict);
      return district[0]['zip'];
    },

    isValid() {
      return !this.contactLastNameError
      && !this.contactFirstNameError
      && !this.contactEmailError
      && !this.contactPhoneError
      && !this.donationOwnerError
      && !this.receiptTypeError
      && !this.receiptTitleError
      && !this.receiptIdNoError
      && !this.receiptMailingCityError
      && !this.receiptMailingDistrictError
      && !this.receiptZipCodeError
      && !this.receiptMailingAddressError
      && !this.showOnDonationListError
      && !this.personalDataConsentError;
    },

    errorMessage() {
      return !this.isValid ? '請檢查以上欄位是否填寫正確！' : '';
    },
  },

  watch: {
    'receipt.idNo'(newVal) {
      // 自動上傳國稅局抵減所得稅額
      this.receipt.etaxCheck = isValidTwID(newVal);
      this.validTwId = isValidTwID(newVal);
    },
    donationOwner() {
      this.resetError();
      this.receipt.etaxCheck = false;
    },
  },

  mounted() {
    this.updateStep('1');
    this.resetError();
  },

  methods: {
    ...mapMutations({
      updateStep: 'breadCrumb/updateStep',
      openOverlay: 'overlay/open',
      closeOverlay: 'overlay/close',
    }),

    loadDonationUser() { // 同捐款人
      this.receipt.title = this.donationOwner === '個人名義'
        ? this.contact.lastName + this.contact.firstName
        : this.contact.groupName;

      // 自動上傳國稅局
      if(this.donationOwner === '個人名義' && isValidID(this.receipt.idNo)) {
        this.receipt.etaxCheck = true;
      }
    },

    sendGA() {
      const GA_DONATION_INFO = DONATION_WAY_TO_DONATION_GA_INFO[this.donationWay];
      if (!GA_DONATION_INFO) {
        console.warn('Unknown donation way:', this.donationWay);
        return;
      }
      const GA_PARM = {
        ecommerce: {
          currency: 'TWD',
          value: parseFloat(this.amount),
          items: [GA_DONATION_INFO['item']],
        },
      };
      this.$gtag.event('TD_begin_checkout', GA_PARM);
    },

    resetError() {
      this.contactLastNameError = '';
      this.contactFirstNameError = '';
      this.contactEmailError = '';
      this.contactPhoneError = '';
      this.contactGroupTitleError = '';
      this.donationOwnerError = '';

      this.receiptTypeError = '';
      this.receiptTitleError = '';
      this.receiptIdNoError = '';
      this.receiptMailingCityError = '';
      this.receiptMailingDistrictError = '';
      this.receiptZipCodeError = '';
      this.receiptMailingAddressError = '';
      this.showOnDonationListError = false;
    },

    checkForErrors() {
      const { contact } = this;

      if(this.donationOwner === '匿名') {
        return;
      }

      if(!this.donationOwner) {
        this.donationOwnerError = '請選擇捐款名義';
      }

      const errorMessageSpecialSymbols = '，並且不要使用全形符號、空格、或,+!?/()等符號。';

      if (!isValidEmail(contact.email)) {
        this.contactEmailError = '請輸入正確的 Email';
      } else {
        this.contactEmailError = null;
      }

      if (!isValidString(contact.phone)) {
        this.contactPhoneError = '請輸入手機號碼' + errorMessageSpecialSymbols;
      } else if(!isValidPhone(contact.phone)) {
        this.contactPhoneError = '請輸入正確手機號碼格式（半形數字）';
      } else {
        this.contactPhoneError = null;
      }

      if (!isValidString(contact.lastName)) {
        this.contactLastNameError = '請輸入聯絡人姓氏' + errorMessageSpecialSymbols;
      } else if(!isValidName(contact.lastName)) {
        this.contactLastNameError = '請輸入正確的姓氏(不可含數字/空格)';
      } else {
        this.contactLastNameError = null;
      }

      if (!isValidString(contact.firstName)) {
        this.contactFirstNameError = '請輸入聯絡人名字' + errorMessageSpecialSymbols;
      } else if(!isValidName(contact.firstName)) {
        this.contactFirstNameError = '請輸入正確的名字(不可含數字/空格)';
      } else {
        this.contactFirstNameError = null;
      }

      // 團體名義
      if(this.donationOwner == '團體名義') {
        if (!isValidString(contact.groupName)) {
          this.contactGroupTitleError = '請輸入單位名稱' + errorMessageSpecialSymbols;
        } else {
          this.contactGroupTitleError = null;
        }
      }

      // 共用收據欄位檢查
      if (!isValidOption(this.receipt.type)) {
        this.receiptTypeError = '請選擇收據種類' + errorMessageSpecialSymbols;
      } else {
        this.receiptTypeError = null;
      }

      if([receiptType.YEAR_PAPER_RECEIPT,
        receiptType.YEAR_ELEC_RECEIPT,
        receiptType.SINGLE_ELEC_RECEIPT].includes(this.receipt.type)) {
        if (!isValidString(this.receipt.title)) {
          this.receiptTitleError = '請輸入收據抬頭' + errorMessageSpecialSymbols;
        } else {
          this.receiptTitleError = null;
        }
      }

      if(this.receipt.etaxCheck) {
        if (!isValidString(this.receipt.idNo)) {
          this.receiptIdNoError = '請輸入收據身分證字號' + errorMessageSpecialSymbols;
        } else {
          this.receiptIdNoError = null;
        }
      }

      if(this.receipt.type === receiptType.YEAR_PAPER_RECEIPT) {
        if (!isValidOption(this.receipt.mailingCity)) {
          this.receiptMailingCityError = '請選擇縣市';
        } else {
          this.receiptMailingCityError = null;
        }

        if (!isValidOption(this.receipt.mailingDistrict)) {
          this.receiptMailingDistrictError = '請選擇鄉鎮市區';
        } else {
          this.receiptMailingDistrictError = null;
        }

        if (!isValidString(this.receipt.mailingZipCode)) {
          this.receiptZipCodeError = '請輸入郵遞區號';
        } else {
          this.receiptZipCodeError = null;
        }

        if (!isValidString(this.receipt.mailingAddress)) {
          this.receiptMailingAddressError = '請輸入詳細的地址' + errorMessageSpecialSymbols;
        } else if(!isValidAddress(this.receipt.mailingAddress)) {
          this.receiptMailingAddressError = '請確認詳細地址是否完整，並且不要使用全形數字符號或空格';
        } else {
          this.receiptMailingAddressError = null;
        }
      }

      if(!this.showOnDonationList) {
        this.showOnDonationListError = '不同意也需勾選，並依左側說明回傳聲明書';
      } else {
        this.showOnDonationListError = null;
      }

      if(!this.isValidPersonalDataConsent()) {
        this.personalDataConsentError = '非匿名捐款須勾選同意';
      } else {
        this.personalDataConsentError = null;
      }
    },

    isValidPersonalDataConsent() {
      if(this.donationOwner !== '匿名') {
        return !!this.personalDataConsent;
      }
      return true;
    },

    back() {
      this.$router.push(
        { name: this.donationPeriod === donationPeriodType.DONATION_SINGLE ? 'single-amount' : 'period-amount' });
    },

    submit() {
      saveStore();
      this.checkForErrors();
      if(this.isValid) {
        // Set Sentry user data after user inputs information
        Sentry.setUser({
          email: this.contact.email,
          username: this.contact.lastName + this.contact.firstName,
          id: this.contact.phone,
        });
        // Meta Pixel 進階配對：推使用者資料進 dataLayer，供 GTM 的 FB Pixel 代碼使用
        pushUserDataToDataLayer(this.contact);
        this.sendGA();
        this.$router.push({ name: this.donationPeriod === donationPeriodType.DONATION_SINGLE ? 'single-confirm' : 'period-confirm' });
      }
    },
  },
};
</script>

<style lang="scss" module>
.text {
  font-size: 14px;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.75;
  letter-spacing: 0.8px;
  color: $color-text;
  margin: 10px auto;
}

.checkBoxRadioWrap {
  margin: 16px auto;
}
</style>
